import type { ReactNode } from "react";

import { Card, PageHead, Pill, Section, Table, Td, Th } from "@/components/kit";

import styles from "./docs.module.css";

/**
 * The whole thing, end to end, followed through one sport.
 *
 * Written before screen 1 is built, so that what gets built is what was agreed.
 * Cricket is the worked example because it is the hardest of the common cases:
 * four formats, several competition shapes, and squads that belong to a
 * tournament rather than to a club.
 */

function Step({
  n,
  title,
  note,
  children,
}: {
  n: number;
  title: string;
  note: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.step}>
      <span className={styles.num}>{n}</span>
      <h3 className={styles.stepTitle}>{title}</h3>
      <p className={styles.stepNote}>{note}</p>
      {children}
    </div>
  );
}

function Code({ label, children }: { label?: string; children: string }) {
  return (
    <div>
      {label && <p className={styles.codeLabel}>{label}</p>}
      <pre className={styles.code}>{children}</pre>
    </div>
  );
}

const DEFINITION = `{
  "sportCode": "cricket",
  "name": "Cricket",
  "partOf": null,

  "masters": [
    { "code": "team",   "label": "Team",   "binding": "participant",
      "inSports": ["cricket"],
      "fields": [
        { "key": "shortName", "type": "TEXT" },
        { "key": "founded",   "type": "INTEGER" } ] },

    { "code": "player", "label": "Player", "binding": "participant",
      "inSports": ["cricket"],
      "fields": [
        { "key": "battingStyle", "type": "ENUM",
          "options": ["Right hand", "Left hand"] },
        { "key": "bowlingStyle", "type": "TEXT" } ],
      "memberOf": [ { "master": "team" } ] },

    { "code": "country", "label": "Country", "binding": "participant",
      "inSports": [] },                          // [] means every sport

    { "code": "ground", "label": "Ground", "binding": "venue",
      "inSports": [] }
  ],

  "fixtureTypes": [
    { "code": "match", "label": "Match", "outcome": "DUEL",
      "entrant":     { "master": "team", "min": 2, "max": 2 },
      "composition": { "master": "player", "min": 11, "max": 11 },
      "alignments":  ["Home", "Away"],
      "officials":   [ { "master": "umpire", "role": "umpire",
                         "min": 2, "max": 3 } ],
      "format": [
        { "key": "overs",         "type": "INTEGER" },
        { "key": "ballsPerOver",  "type": "INTEGER" },
        { "key": "inningsPerSide","type": "INTEGER" },
        { "key": "days",          "type": "INTEGER" } ] }
  ],

  "configs": [
    { "code": "t20",  "label": "T20",  "appliesTo": ["match"],
      "values": { "overs": 20, "ballsPerOver": 6,
                  "inningsPerSide": 1, "days": 1 } },
    { "code": "test", "label": "Test", "appliesTo": ["match"],
      "values": { "ballsPerOver": 6, "inningsPerSide": 2, "days": 5 } },
    { "code": "hundred", "label": "The Hundred", "appliesTo": ["match"],
      "addFields": [ { "key": "balls", "type": "INTEGER" },
                     { "key": "ballsPerSet", "type": "INTEGER" } ],
      "dropFields": ["overs"],
      "values": { "balls": 100, "ballsPerSet": 5,
                  "inningsPerSide": 1, "days": 1 } }
  ],

  "levels": [
    { "code": "tour",    "label": "Tour",
      "canBeTop": true,  "holds": ["series", "match"], "hasFixtures": true },
    { "code": "series",  "label": "Series",
      "canBeTop": false, "holds": [],        "hasFixtures": true },
    { "code": "league",  "label": "League",
      "canBeTop": true,  "holds": ["season"], "hasFixtures": false },
    { "code": "season",  "label": "Season",
      "canBeTop": false, "holds": ["stage"],  "hasFixtures": false },
    { "code": "stage",   "label": "Stage",
      "canBeTop": false, "holds": [],         "hasFixtures": true }
  ]
}`;

const PARTICIPANT_ROW = `participant
  id          019423e1-…            (uuid, generated)
  code        "rohit-sharma"        (the public id, typed or derived)
  type        PERSON
  name        "Rohit Sharma"
  country     "IND"
  meta        { "battingStyle": "Right hand",
                "bowlingStyle": "Right arm off break" }

participant_sport                   ← new (change 1)
  participant_id  019423e1-…
  sport_id        <cricket>`;

const COMPETITION_ROWS = `competition
  code            "wc-2027"     type "league"  parent  —
  code            "wc-2027-gs"  type "season"  parent  wc-2027
  code            "wc-2027-s8"  type "stage"   parent  wc-2027-gs
                                               config  { }
                                               format  "t20"   ← new (change 1)`;

const FIXTURE_ROWS = `fixture
  code                 "ind-v-aus-2027-03-11"
  sport_id             <cricket>
  competition_id       <wc-2027-s8>
  fixture_type         "match"           which kind
  format_code          "t20"             ← new: which config
  archetype            HEAD_TO_HEAD      derived, not typed

  format               the rules — written by the config, not typed
                       { "overs": 20, "ballsPerOver": 6,
                         "inningsPerSide": 1, "days": 1 }

  meta                 ← new: this fixture, typed by the operator
                       { "matchNumber": 24,
                         "attendance": 41203,
                         "pitchReport": "Dry, takes turn",
                         "broadcaster": "Sky Sports" }

  definition_version_id <cricket v7>     the rules it was entered under

fixture_competitor                       one per side
  participant_id  <India>    alignment "Home"  ordinal 1
  participant_id  <Australia> alignment "Away" ordinal 2

fixture_competitor                       one per player, nested
  parent_id  <India's row>   participant_id <Rohit>  ordinal 1`;

const MASTERS = [
  ["Team, Player, Umpire, Country", "participant", "participant.meta"],
  ["Tour, Series, League, Season, Stage", "competition", "competition.config"],
  ["Ground", "venue", "venue.meta"],
  ["Transfer", "transfer", "—"],
  ["Former name", "name_history", "—"],
  ["Photo", "media", "—"],
  ["Ways a side can be out", "outcome_codes", "—"],
];

const CHANGES = [
  ["A record lists its sports", "new table participant_sport", "1 — before any screen", "bad"],
  ["Sports can group", "sport.parent_id", "1 — before any screen", "bad"],
  ["Configs", "competition.format_code, fixture.format_code", "5 — with the fixture picker", "warn"],
  ["Fixture's own details", "fixture.meta", "5 — with the fixture form", "good"],
  [
    "fixture_competitor.rank — NOT dropped",
    "it is the only record of who won a duel",
    "kept; the cleanup list was wrong",
    "warn",
  ],
  ["Levels and their rules", "nothing — it lives in the document", "4 — with the tree", "good"],
  ["Squads", "participant_membership.competition_id", "4 — with the squad screen", "warn"],
  ["A Games with no sport", "competition.sport_id becomes optional", "8 — with Events", "warn"],
  ["Belongs to, without being inside", "competition.gathers_id", "8 — with Events", "warn"],
  ["Venues have parts", "venue.parent_id", "2 — with the venue list", "good"],
];

const API = [
  ["GET", "/definitions/{sport}", "read a sport", "exists", "good"],
  ["GET", "/definitions/{sport}/draft", "read the unsaved draft", "exists", "good"],
  ["POST", "/definitions", "save a draft", "exists", "good"],
  ["POST", "/definitions/{sport}/versions/{n}/publish", "publish", "exists", "good"],
  ["GET", "/sports/{sport}/masters", "what this sport keeps", "exists", "good"],
  ["GET", "/sports/{sport}/masters/{m}/records", "the list", "needs search + filter", "warn"],
  ["POST", "/sports/{sport}/masters/{m}/records", "add one", "exists", "good"],
  ["GET", "/sports/{sport}/masters/{m}/csv-template", "the blank sheet", "exists", "good"],
  ["POST", "/sports/{sport}/masters/{m}/csv", "import", "all-or-nothing today", "bad"],
  ["GET", "/competitions?sport=", "the tree", "exists, flat", "warn"],
  ["GET", "/sports/{sport}/fixture-form", "what to draw", "exists", "good"],
  ["POST", "/fixtures", "write a fixture", "exists", "good"],
];

export function FlowPage() {
  return (
    <div className={styles.page}>
      <PageHead
        title="How it all works, end to end"
        note="One sport followed from someone typing it in to a fixture being entered — and where every piece of it lands in the database. Agreed before screen 1 is built."
      />

      <Section
        title="The one idea"
        note="Everything below is a consequence of this, so it is worth reading twice."
      >
        <div className={styles.claim}>
          <p>
            <strong>Nothing about cricket becomes a column.</strong> A sport is one document. The
            database holds seven fixed tables that every sport shares, and the sport&apos;s own
            details ride inside them as loose data.
          </p>
          <p>
            That is what lets any sport be added at any time, by an operator, with no engineering
            work and no database change. It is also why every screen has to be drawn from the
            document rather than written by hand.
          </p>
        </div>
      </Section>

      <Section title="The walkthrough" note="Cricket, because it is the hardest of the common cases.">
        <Step
          n={1}
          title="Someone fills in the sport editor"
          note="One page, top to bottom. It saves as you type; publishing is a separate button. Six sections, in the order a person would actually think about them."
        >
          <Table>
            <thead>
              <tr>
                <Th>Section</Th>
                <Th>What they type</Th>
                <Th>Cricket, for example</Th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Name it", "Code, name, which sport it sits inside", "cricket · Cricket · —"],
                [
                  "What it keeps",
                  "The things, their details, which sports each is in",
                  "Team · Player · Umpire · Country · Ground",
                ],
                [
                  "Kinds of fixture",
                  "How it is decided, who enters, what is asked",
                  "Match — two teams, eleven players each",
                ],
                ["Configs", "Same shape, different rules", "T20 · ODI · Test · The Hundred"],
                [
                  "Competition levels",
                  "What sits inside what, which levels hold fixtures",
                  "Tour › Series · League › Season › Stage",
                ],
                ["Live state", "What changes during play", "Runs, wickets, overs"],
              ].map(([section, what, eg]) => (
                <tr key={section}>
                  <Td strong>{section}</Td>
                  <Td>{what}</Td>
                  <Td quiet>{eg}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Step>

        <Step
          n={2}
          title="It becomes one document"
          note="Everything typed above turns into a single piece of JSON. This is the whole sport — there is nothing else about cricket anywhere in the system."
        >
          <Code label="the sport definition, trimmed">{DEFINITION}</Code>
          <div className={styles.claim}>
            <p>
              Two things in there are new: <span className={styles.mono}>configs</span> (change 1
              from stage 2) and <span className={styles.mono}>levels</span> (change 3). Both are
              just more document — neither needs a database change.
            </p>
          </div>
        </Step>

        <Step
          n={3}
          title="The document is saved as a version"
          note="Two tables. One row per sport, and one row per save. This already works exactly like this today."
        >
          <Code>{`sport_definition            one row per sport
  sport_code   "cricket"

sport_definition_version    one row per save
  version      7
  status       DRAFT | PUBLISHED
  schema       { the whole document above }
  published_at 2026-07-29T…`}</Code>
          <p className={styles.stepNote}>
            Typing writes a draft, overwritten as you go. Publishing cuts a numbered version that
            is never edited again — so a fixture entered last March can still say exactly which
            rules it was entered under.
          </p>
        </Step>

        <Step
          n={4}
          title="“What it keeps” does not create tables"
          note="A master is a binding onto one of seven fixed tables. The operator invents the name and the details; we decide nothing."
        >
          <Table>
            <thead>
              <tr>
                <Th>What the operator invents</Th>
                <Th>The table it lands in</Th>
                <Th>Where its own details live</Th>
              </tr>
            </thead>
            <tbody>
              {MASTERS.map(([invented, table, extras]) => (
                <tr key={table}>
                  <Td strong>{invented}</Td>
                  <Td>
                    <Pill tone="accent">{table}</Pill>
                  </Td>
                  <Td quiet>{extras}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Step>

        <Step
          n={5}
          title="A player is added"
          note="The form is drawn from the master's own fields, in the order they were written. What is saved is one ordinary row plus a bag of that sport's details."
        >
          <Code label="one player">{PARTICIPANT_ROW}</Code>
          <p className={styles.stepNote}>
            The second table is the change we agreed: a record lists its sports. Rohit lists
            cricket. India, as a country, lists none — which is how one India is seen by every
            sport and a medal table becomes possible.
          </p>
        </Step>

        <Step
          n={6}
          title="Competitions are built"
          note="One table that points at itself, so any shape and any depth works. The level names are the operator's, from the document."
        >
          <Code label="the 2027 World Cup">{COMPETITION_ROWS}</Code>
          <p className={styles.stepNote}>
            The rules in <span className={styles.mono}>levels</span> decide what “add inside”
            offers, so a wrong shape cannot be built. The config set here is inherited by every
            fixture below it.
          </p>
        </Step>

        <Step
          n={7}
          title="A fixture is entered"
          note="One choice — T20 — then the form. The kind says what shape it is; the config says which rules."
        >
          <Code label="India v Australia">{FIXTURE_ROWS}</Code>
          <p className={styles.stepNote}>
            The values in <span className={styles.mono}>format</span> are copied from the config at
            the moment of entry, not looked up later. Changing the T20 config next season must not
            silently rewrite last season&apos;s matches.
          </p>
          <div className={styles.claim}>
            <p>
              <strong>
                <span className={styles.mono}>meta</span> is separate from{" "}
                <span className={styles.mono}>format</span> on purpose.
              </strong>{" "}
              Rules come from the config and are shared by every T20 match ever played. Attendance,
              the pitch report and the match number are about this one fixture and are typed by a
              person.
            </p>
            <p>
              Keeping them in one bag would mean never being able to tell which is which — so a
              changed config could not be safely re-applied, and the form could not show what was
              inherited and what was yours.
            </p>
            <p>
              What goes in <span className={styles.mono}>meta</span> is still declared by the
              fixture kind, the same as everything else. A declared field gets a control, a filter,
              a list column and a CSV column for free. An undeclared key would appear on no screen
              at all.
            </p>
          </div>
        </Step>

        <Step
          n={8}
          title="Every screen asks the same question"
          note="There is no per-sport code anywhere. Each screen reads the document and draws itself."
        >
          <Code>{`the document
     │
     ├── what it keeps      →  the data lists, their columns,
     │                         their filters, the CSV template
     │
     ├── kinds of fixture   →  the fixture form: who enters,
     │                         what is asked before and after
     │
     ├── configs            →  the one-step picker on a new fixture
     │
     ├── levels             →  the competition tree, and what
     │                         "add inside" is allowed to offer
     │
     └── live state         →  handed to the scorer UI, which is
                               a separate layer and not ours`}</Code>
        </Step>
      </Section>

      <Section
        title="What has to change in the database"
        note="Nine changes. Seven are a single column. Nothing here is a rewrite."
      >
        <Table>
          <thead>
            <tr>
              <Th>Change</Th>
              <Th>What it is</Th>
              <Th>When it lands</Th>
              <Th>Reach</Th>
            </tr>
          </thead>
          <tbody>
            {CHANGES.map(([change, what, when, tone]) => (
              <tr key={change}>
                <Td strong>{change}</Td>
                <Td quiet>
                  <span className={styles.mono}>{what}</span>
                </Td>
                <Td>{when}</Td>
                <Td>
                  <Pill tone={tone as "good" | "warn" | "bad"} dot>
                    {tone === "bad" ? "touches everything" : tone === "warn" ? "contained" : "small"}
                  </Pill>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>

      <Section
        title="The API"
        note="Most of it already exists. What is missing is search, and an import that does not give up on the whole file."
      >
        <Table>
          <thead>
            <tr>
              <Th>Method</Th>
              <Th>Path</Th>
              <Th>What it does</Th>
              <Th>State</Th>
            </tr>
          </thead>
          <tbody>
            {API.map(([method, path, does, state, tone]) => (
              <tr key={`${method}-${path}`}>
                <Td strong>{method}</Td>
                <Td quiet>
                  <span className={styles.mono}>{path}</span>
                </Td>
                <Td>{does}</Td>
                <Td>
                  <Pill tone={tone as "good" | "warn" | "bad"} dot={tone !== "good"}>
                    {state}
                  </Pill>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>

      <Section title="Three things to settle before building">
        <div className={styles.stack}>
          <Card>
            <div className={styles.flag}>
              <p>
                <strong>Import is all-or-nothing today.</strong> One bad row in a file of 500 and
                nothing is written. The design we agreed says the good rows wait while you fix the
                bad ones.
              </p>
              <p>
                Changing it is not hard, but it changes what “imported” means — you can end a
                session half done. Worth deciding on purpose.
              </p>
            </div>
          </Card>
          <Card>
            <div className={styles.flag}>
              <p>
                <strong>Search does not exist.</strong> Custom details live as loose data, which is
                what makes a new sport free. It is also slower to search.
              </p>
              <p>
                You said small now, millions later. So the list endpoint must never leak how things
                are stored — then a sport that grows can have its searchable details moved into real
                columns without a single screen changing.
              </p>
            </div>
          </Card>
          <Card>
            <div className={styles.flag}>
              <p>
                <strong>The competition list is flat.</strong> It returns rows, not a tree. The tree
                screen needs the shape, the path to each node, and what each is allowed to hold.
              </p>
            </div>
          </Card>
        </div>
      </Section>
    </div>
  );
}
